// src/products/dataroom/services/projectService.js
// Focused service for Dataroom project management

import { supabase } from '../../../config';
import { cleanUrlName, generateToken } from '../utils/slugUtils';

class ProjectService {
  /**
   * Create a new dataroom project
   */
  async createProject(projectData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check subscription restrictions
      const subscriptionRestrictionsService = (await import('../../../services/subscriptionRestrictionsService')).default;
      const { data: existingProjects } = await supabase
        .from('dataroom_users')
        .select('project_id')
        .eq('user_id', user.id)
        .eq('role', 'creator');
      
      const canCreate = await subscriptionRestrictionsService.canCreateMore(
        user.id, 
        'dataroom', 
        existingProjects?.length || 0
      );

      if (!canCreate.allowed) {
        throw new Error(canCreate.reason || 'You have reached the limit for creating dataroom projects on your plan.');
      }

      // Generate URL slug and token
      let urlSlug = null;
      let urlToken = null;
      
      if (projectData.urlName) {
        urlSlug = cleanUrlName(projectData.urlName);
        if (urlSlug) {
          // Generate unique token
          urlToken = generateToken();
          
          // Check if this slug+token combination already exists (very unlikely but check anyway)
          let attempts = 0;
          while (attempts < 10) {
            const { data: existing, error: checkError } = await supabase
              .from('dataroom_projects')
              .select('id')
              .eq('url_slug', urlSlug)
              .eq('url_token', urlToken)
              .maybeSingle();
            
            // If no error and no existing record, combination is unique
            if (!checkError && !existing) break;
            
            // If error is "not found" (PGRST116), that's fine - combination is unique
            if (checkError && checkError.code === 'PGRST116') break;
            
            // Otherwise, generate new token and try again
            urlToken = generateToken();
            attempts++;
          }
        }
      }

      const cleanProjectData = {
        project_name: projectData.name.trim(),
        description: projectData.description?.trim() || null,
        color: projectData.color || '#3B82F6',
        status: projectData.status || 'active',
        settings: projectData.settings || {},
        url_slug: urlSlug,
        url_token: urlToken,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('dataroom_projects')
        .insert(cleanProjectData)
        .select()
        .single();

      if (error) throw error;

      // Add the creator to the project
      const { error: userError } = await supabase
        .from('dataroom_users')
        .insert({
          user_id: user.id,
          project_id: data.id,
          role: 'creator',
          status: 'active'
        });

      if (userError) {
        console.warn('Failed to add creator to project:', userError);
      }

      // Log activity
      try {
        await this.logActivity(data.id, 'project_created', `Project "${data.project_name}" created`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating project:', error);
      return { data: null, error };
    }
  }

  /**
   * Update project
   */
  async updateProject(projectId, projectData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const cleanProjectData = {
        ...projectData,
        updated_at: new Date().toISOString()
      };

      // Check if user has permission to update project
      const { data: userRole } = await supabase
        .from('dataroom_users')
        .select('role')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      if (!userRole || !['creator', 'admin'].includes(userRole.role)) {
        throw new Error('Insufficient permissions to update project');
      }

      const { data, error } = await supabase
        .from('dataroom_projects')
        .update(cleanProjectData)
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw error;

      // Log activity
      try {
        await this.logActivity(projectId, 'project_updated', `Project "${data.project_name}" updated`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating project:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user's projects
   */
  async getUserProjects(userId) {
    try {
      console.log('Fetching projects for user:', userId);
      
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select(`
          *,
          dataroom_users!inner(role)
        `)
        .eq('dataroom_users.user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        // If table doesn't exist or user doesn't have access, return empty array
        if (error.code === '42P01' || error.code === '42703') {
          console.log('Table or column does not exist, returning empty array');
          return { data: [], error: null };
        }
        throw error;
      }

      console.log('Projects fetched successfully:', data);
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Return empty array instead of null to prevent errors
      return { data: [], error };
    }
  }

  /**
   * Get project by ID
   */
  async getProject(projectId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('dataroom_projects')
        .select(`
          *,
          dataroom_users!inner(role)
        `)
        .eq('id', projectId)
        .eq('dataroom_users.user_id', user.id)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching project:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete project
   */
  async deleteProject(projectId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check if user has permission to delete (creator or admin role)
      const { data: userRole } = await supabase
        .from('dataroom_users')
        .select('role')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      if (!userRole || !['creator', 'admin'].includes(userRole.role)) {
        throw new Error('Insufficient permissions to delete project');
      }

      const { error } = await supabase
        .from('dataroom_projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      return { data: { success: true }, error: null };
    } catch (error) {
      console.error('Error deleting project:', error);
      return { data: null, error };
    }
  }

  /**
   * Archive/unarchive project
   */
  async toggleProjectStatus(projectId, status) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check if user has permission to update project
      const { data: userRole } = await supabase
        .from('dataroom_users')
        .select('role')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      if (!userRole || !['creator', 'admin'].includes(userRole.role)) {
        throw new Error('Insufficient permissions to update project');
      }

      const { data, error } = await supabase
        .from('dataroom_projects')
        .update({ 
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw error;

      // Log activity
      try {
        await this.logActivity(projectId, 'project_status_changed', `Project status changed to ${status}`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error toggling project status:', error);
      return { data: null, error };
    }
  }

  /**
   * Get project statistics
   */
  async getProjectStats(projectId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get file count
      const { count: fileCount, error: fileError } = await supabase
        .from('dataroom_items')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .eq('is_folder', false);

      if (fileError) throw fileError;

      // Get folder count
      const { count: folderCount, error: folderError } = await supabase
        .from('dataroom_items')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .eq('is_folder', true);

      if (folderError) throw folderError;

      // Get user count
      const { count: userCount, error: userError } = await supabase
        .from('dataroom_users')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId);

      if (userError) throw userError;

      // Get total size
      const { data: items, error: sizeError } = await supabase
        .from('dataroom_items')
        .select('size')
        .eq('project_id', projectId)
        .eq('is_folder', false);

      if (sizeError) throw sizeError;

      const totalSize = items.reduce((sum, item) => sum + (parseInt(item.size) || 0), 0);

      const stats = {
        fileCount: fileCount || 0,
        folderCount: folderCount || 0,
        userCount: userCount || 0,
        totalSize: totalSize
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching project stats:', error);
      return { data: null, error };
    }
  }

  /**
   * Log activity for project operations
   */
  async logActivity(projectId, action, description, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('dataroom_logs')
        .insert({
          project_id: projectId,
          user_id: user.id,
          action: action,
          details: {
            description: description,
            ...metadata
          }
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging activity:', error);
      throw error;
    }
  }
}

const projectService = new ProjectService();
export default projectService;
